// Container loading (Logistics → Shipment Control → a shipment's Container loading): how a shipment's cartons fill a
// 20ft / 40ft / 40ft High Cube container. Cartons per line = shipment quantity ÷ pieces per carton, rounded up (or the
// packing list's own carton count); carton size from the shipment, the garment's Packing tab, or a standard export carton.
// Cartons are loaded PO by PO from the back wall towards the doors, in walls (columns floor to roof, across the width),
// always "this way up". Max fill (the default) lets the next PO fill the room a PO leaves in its last walls; "Keep POs
// in blocks" gives each PO its own walls. Fragile cartons never have a non-fragile carton on top. Shared by the server (which sends the lines) and the screen
// (which works the load out again while people try other numbers).
import type { ContainerType } from "./orders.js";

/** A standard export carton (cm), used when a line has no carton size. */
export const STANDARD_CARTON = { l: 60, w: 40, h: 40 } as const;
/** One colour per PO, in PO order. Used on the Container loading screen only. */
export const PO_COLOURS = ["#3b82f6", "#f59e0b", "#10b981", "#ec4899", "#8b5cf6", "#14b8a6", "#ef4444", "#84cc16", "#0ea5e9", "#f97316"] as const;
export const poColour = (i: number) => PO_COLOURS[((i % PO_COLOURS.length) + PO_COLOURS.length) % PO_COLOURS.length];

export type CartonsFrom = "packing" | "estimate" | "none";
export type SizeFrom = "shipment" | "product" | "standard";
/** One shipment line as the loader sees it. */
export interface LoadLine {
  key: string; poId: string; poNumber: string; code: string; description: string; colour: string; qty: number; unit: string;
  pcsPerCarton: number; l: number; w: number; h: number; grossKg: number;
  /** The packing list's carton count (0 = no packing list yet: cartons are worked out from pieces per carton). */
  packedCartons: number; sizeFrom: SizeFrom;
  /** Fragile: nothing that isn't fragile goes on top (fragile on fragile is fine). From the garment's Packing tab or the PO line. */
  fragile?: boolean;
  /** At most this many cartons on top (0 = no limit). */
  maxOnTop?: number;
}
export interface LoadBox { line: number; po: number; x: number; y: number; z: number; dx: number; dy: number; dz: number; fragile?: boolean; wall?: number }

const pos = (v: unknown) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : 0; };
/** Cartons for a quantity: the packing list's count, else quantity ÷ pieces per carton rounded up. */
export function lineCartons(l: Pick<LoadLine, "qty" | "pcsPerCarton" | "packedCartons">): { cartons: number; from: CartonsFrom; partLast: number } {
  if (pos(l.packedCartons)) return { cartons: Math.round(pos(l.packedCartons)), from: "packing", partLast: 0 };
  const ppc = Math.floor(pos(l.pcsPerCarton)), qty = pos(l.qty);
  if (!ppc || !qty) return { cartons: 0, from: "none", partLast: 0 };
  return { cartons: Math.ceil(qty / ppc), from: "estimate", partLast: Math.round(qty) % ppc };
}
export const cartonCbm = (l: Pick<LoadLine, "l" | "w" | "h">) => (pos(l.l) * pos(l.w) * pos(l.h)) / 1_000_000;

/** Inside size of a container in cm. */
export const insideCm = (c: ContainerType) => ({ L: Math.round(c.internalLengthM * 100), W: Math.round(c.internalWidthM * 100), H: Math.round(c.internalHeightM * 100) });
export const containerCbm = (c: ContainerType) => c.internalLengthM * c.internalWidthM * c.internalHeightM;

/** How the cartons are arranged: "max" (the default) lets the next PO fill the gaps a PO leaves; "blocks" gives each PO its own walls. */
export type LoadMode = "max" | "blocks";
export const LOAD_MODES: ReadonlyArray<readonly [LoadMode, string]> = [["max", "Max fill"], ["blocks", "Keep POs in blocks"]];
export const DEFAULT_LOAD_MODE: LoadMode = "max";
export const LOAD_MODE_LABEL: Record<LoadMode, string> = { max: "Max fill", blocks: "Keep POs in blocks" };
export const isLoadMode = (v: unknown): v is LoadMode => v === "max" || v === "blocks";

/** A wall: one slice of the container across its width, floor to roof, from the back wall towards the doors. */
export interface LoadWall { n: number; x: number; depth: number; pos: number[]; cartons: number[]; fragile: number; shared: boolean }

interface Stack { x: number; z: number; sdx: number; sdz: number; top: number; fragile: boolean; room: number }
interface Slice { n: number; x: number; depth: number; zUsed: number; owner: number; stacks: Stack[] }
const NO_LIMIT = Number.POSITIVE_INFINITY;
const limitOf = (l: Pick<LoadLine, "maxOnTop">) => { const m = Math.floor(pos(l.maxOnTop)); return m > 0 ? m : NO_LIMIT; };

/**
 * Load the lines into one container. Cartons always stay "this way up" and may turn on the floor. Rules for stacking:
 * a carton only goes on top of a carton it fully sits on; nothing that isn't fragile goes on top of a fragile carton
 * (fragile on fragile is fine); and a carton with "max cartons on top" never has more than that above it.
 *  - Max fill (the default): PO by PO from the back wall, but a PO's cartons first fill any room left in the walls
 *    already started (on top of part-full stacks, then beside them) before starting a new wall, so no wall is left
 *    part-empty between POs. Fragile lines are loaded after the others so they sit on top or nearest the doors.
 *  - Keep POs in blocks: every PO starts its own walls, so each PO comes off together (part-full walls stay empty).
 * Returns every carton's place (cm, from the back wall's left floor corner), what fitted per line, the walls, and the
 * floor length used.
 */
export function loadContainer(c: ContainerType, lines: LoadLine[], mode: LoadMode = DEFAULT_LOAD_MODE) {
  const { L, W, H } = insideCm(c);
  const boxes: LoadBox[] = [];
  const poIndex = new Map<string, number>();
  for (const ln of lines) if (!poIndex.has(ln.poId)) poIndex.set(ln.poId, poIndex.size);
  const poOf = (i: number) => poIndex.get(lines[i].poId)!;
  // Loading order: max fill → normal lines (PO order), then fragile lines; blocks → PO by PO, fragile last within a PO.
  const order = lines.map((_, i) => i).sort((a, b) => {
    const fa = lines[a].fragile ? 1 : 0, fb = lines[b].fragile ? 1 : 0;
    return mode === "max" ? (fa - fb) || (a - b) : (poOf(a) - poOf(b)) || (fa - fb) || (a - b);
  });
  const slices: Slice[] = [];
  let xEnd = 0;
  const per = lines.map((ln) => ({ cartons: lineCartons(ln).cartons, loaded: 0, cbm: 0, totalCbm: 0, grossKg: 0, fits: true }));
  for (const i of order) {
    const ln = lines[i], po = poOf(i), owner = mode === "blocks" ? po : -1;
    const { cartons } = per[i];
    const h = pos(ln.h), fragile = !!ln.fragile, limit = limitOf(ln);
    // Turn the carton on the floor whichever way takes less length; when the line doesn't fit either way, whichever
    // way packs the most cartons per metre. That way is tried first; the other way only to fill a gap.
    let best: { dx: number; dz: number; depth: number; all: boolean; density: number } | null = null;
    const turns: Array<[number, number]> = [[pos(ln.l), pos(ln.w)], [pos(ln.w), pos(ln.l)]];
    for (const [dx, dz] of turns) {
      const nz = dz ? Math.floor(W / dz) : 0, ny = h ? Math.floor(H / h) : 0;
      if (!nz || !ny || !dx || !cartons) continue;
      const perWall = limit === NO_LIMIT ? ny : Math.min(ny, limit + 1);
      const depth = Math.ceil(cartons / (nz * perWall)) * dx, o = { dx, dz, depth, all: xEnd + depth <= L + 0.01, density: (nz * perWall) / dx };
      if (!best || (o.all && best.all ? o.depth < best.depth : o.all !== best.all ? o.all : o.density > best.density)) best = o;
    }
    if (!best) { per[i].fits = !cartons; continue; }
    const ways: Array<[number, number]> = best.dx === best.dz ? [[best.dx, best.dz]] : [[best.dx, best.dz], [best.dz, best.dx]];
    const mine = (s: Slice) => mode === "max" || s.owner === owner;
    const place = (s: Slice, st: Stack, dx: number, dz: number) => {
      boxes.push({ line: i, po, x: st.x, y: st.top, z: st.z, dx, dy: h, dz, fragile, wall: s.n });
      st.top += h; st.sdx = dx; st.sdz = dz; st.fragile = st.fragile || fragile;
      st.room = Math.min(st.room - 1, limit);
    };
    let loaded = 0;
    for (let k = 0; k < cartons; k++) {
      let done = false;
      // 1. On top of a stack already started (back wall first), when it fully supports the carton and the rules allow.
      for (const s of slices) {
        if (done || !mine(s)) continue;
        for (const st of s.stacks) {
          if (st.top + h > H + 0.01 || st.room < 1 || (st.fragile && !fragile)) continue;
          const way = ways.find(([dx, dz]) => dx <= st.sdx + 0.01 && dz <= st.sdz + 0.01);
          if (!way) continue;
          place(s, st, way[0], way[1]); done = true; break;
        }
      }
      // 2. A new stack beside the others in a wall already started.
      if (!done) for (const s of slices) {
        if (!mine(s)) continue;
        const way = ways.find(([dx, dz]) => dx <= s.depth + 0.01 && s.zUsed + dz <= W + 0.01 && h <= H + 0.01);
        if (!way) continue;
        const st: Stack = { x: s.x, z: s.zUsed, sdx: way[0], sdz: way[1], top: 0, fragile: false, room: NO_LIMIT };
        s.zUsed += way[1]; s.stacks.push(st);
        place(s, st, way[0], way[1]); done = true; break;
      }
      // 3. A new wall nearer the doors.
      if (!done && xEnd + best.dx <= L + 0.01 && best.dz <= W + 0.01 && h <= H + 0.01) {
        const s: Slice = { n: slices.length + 1, x: xEnd, depth: best.dx, zUsed: best.dz, owner, stacks: [] };
        const st: Stack = { x: xEnd, z: 0, sdx: best.dx, sdz: best.dz, top: 0, fragile: false, room: NO_LIMIT };
        s.stacks.push(st); slices.push(s); xEnd += best.dx;
        place(s, st, best.dx, best.dz); done = true;
      }
      if (!done) break;   // identical cartons: if this one doesn't fit, the rest won't either
      loaded++;
    }
    const each = cartonCbm(ln);
    per[i] = { cartons, loaded, cbm: loaded * each, totalCbm: cartons * each, grossKg: loaded * pos(ln.grossKg), fits: true };
  }
  for (let i = 0; i < lines.length; i++) if (!per[i].totalCbm) per[i].totalCbm = per[i].cartons * cartonCbm(lines[i]);
  const walls: LoadWall[] = slices.map((s) => {
    const mineBoxes = boxes.filter((b) => b.wall === s.n);
    const ps = [...new Set(mineBoxes.map((b) => b.po))].sort((a, b) => a - b);
    return { n: s.n, x: s.x, depth: s.depth, pos: ps, cartons: ps.map((p) => mineBoxes.filter((b) => b.po === p).length), fragile: mineBoxes.filter((b) => b.fragile).length, shared: ps.length > 1 };
  });
  return { boxes, per, walls, mode, usedLength: Math.min(xEnd, L), poCount: poIndex.size };
}

/** The numbers above the 3D view: how full, cartons, space and floor left, weight, and what to do when it doesn't fit. */
export function loadSummary(c: ContainerType, lines: LoadLine[], all: ContainerType[] = [], mode: LoadMode = DEFAULT_LOAD_MODE) {
  const r = loadContainer(c, lines, mode);
  const cap = containerCbm(c);
  const used = r.per.reduce((a, p) => a + p.cbm, 0);
  const total = r.per.reduce((a, p) => a + p.cartons, 0), loaded = r.per.reduce((a, p) => a + p.loaded, 0);
  const grossKg = r.per.reduce((a, p) => a + p.grossKg, 0);
  const weightKnown = lines.length > 0 && lines.every((l) => pos(l.grossKg) > 0);
  const practical = c.planningCbm > 0 && c.planningCbm < cap ? c.planningCbm : cap * 0.85;
  const left = insideCm(c).L - r.usedLength;
  // When cartons don't fit: the next bigger container type they all fit in, if any.
  const bigger = loaded < total ? all.filter((o) => containerCbm(o) > cap).sort((a, b) => containerCbm(a) - containerCbm(b))
    .find((o) => { const x = loadContainer(o, lines, mode); return x.per.every((p) => p.loaded === p.cartons); }) || null : null;
  // The same cartons the other way, to show what max fill saves.
  const other: LoadMode = mode === "max" ? "blocks" : "max";
  const alt = loadContainer(c, lines, other);
  const altLoaded = alt.per.reduce((a, p) => a + p.loaded, 0);
  // Top-up: when everything fits, how many more cartons of the line loaded nearest the doors would still go in.
  let topUp: { line: number; cartons: number } | null = null;
  if (total && loaded === total) {
    const lastBox = r.boxes.reduce<LoadBox | null>((m, b) => (!m || b.x + b.dx > m.x + m.dx || (b.x + b.dx === m.x + m.dx && b.line > m.line) ? b : m), null);
    if (lastBox) {
      const src = lines[lastBox.line];
      const extra = loadContainer(c, [...lines, { ...src, key: `${src.key}~topup`, packedCartons: 5000 }], mode);
      const n = extra.per[lines.length].loaded;
      if (n > 0) topUp = { line: lastBox.line, cartons: n };
    }
  }
  return {
    ...r, capacityCbm: cap, usedCbm: used, fillPct: cap ? (used / cap) * 100 : 0, leftCbm: Math.max(0, cap - used),
    practicalCbm: practical, practicalPct: cap ? (practical / cap) * 100 : 85, roomBeforeFull: Math.max(0, practical - used),
    totalCartons: total, loadedCartons: loaded, notLoaded: total - loaded, floorLeftM: Math.max(0, left) / 100,
    grossKg, weightKnown, overWeight: weightKnown && c.maxPayloadKg > 0 && grossKg > c.maxPayloadKg, bigger,
    sharedWalls: r.walls.filter((w) => w.shared).length, topUp,
    other: { mode: other, floorLeftM: Math.max(0, insideCm(c).L - alt.usedLength) / 100, loadedCartons: altLoaded },
  };
}

/** The container a shipment is planned in: the freight booking ("1 × 40HQ"), else the POs' planned container, else the smallest that fits. */
export function plannedContainer(all: ContainerType[], booked: string, poPlanned: string[], lines: LoadLine[]): string {
  const norm = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const b = norm(booked);
  if (b) {
    const hit = [...all].sort((x, y) => norm(y.uid).length - norm(x.uid).length).find((c) => b.includes(norm(c.uid)) || (norm(c.name) && b.includes(norm(c.name))));
    if (hit) return hit.uid;
    if (/40H[QC]|HIGHCUBE/.test(b)) { const hc = all.find((c) => /40H[QC]|HIGH/.test(norm(c.uid + c.name))); if (hc) return hc.uid; }
  }
  const planned = poPlanned.find((u) => all.some((c) => c.uid === u));
  if (planned) return planned;
  const sorted = [...all].sort((x, y) => containerCbm(x) - containerCbm(y));
  return (sorted.find((c) => { const x = loadContainer(c, lines); return x.per.every((p) => p.loaded === p.cartons); }) || sorted[0])?.uid || "";
}
